import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calculator, TrendingUp, TrendingDown, RefreshCw, Copy, Check, Sliders, ChevronDown, ChevronUp, Search, Target } from 'lucide-react';
import LevelChart from './LevelChart';
import '../styles/GannCalculator.css';

// Storage key for persisting state
const GANN_STORAGE_KEY = 'gann_calculator_state';
const REVERSE_STORAGE_KEY = 'gann_reverse_state';

// Sub-percentages for intermediate levels
const SUB_PCTS = ['87.5', '62.5', '37.5', '12.5'];

// Emoji pairs for matching values - each match pair gets a unique emoji
const MATCH_EMOJIS = ['🔗', '⚡', '💎', '🎯', '🌟', '🔥', '💫', '✨', '🎪', '🎭'];

// Level percentages for reverse engineering
const LEVEL_PERCENTAGES = [12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100];

// Helper: Calculate digit sum
const digitSum = (n) => {
  const nInt = Math.abs(Math.floor(n));
  return String(nInt).split('').reduce((sum, d) => sum + parseInt(d, 10), 0);
};

// Helper: Calculate Gann levels for a given price
const calculateGannLevels = (price, multiplier = 7) => {
  const sqrtPrice = Math.sqrt(price);
  const roundedSqrt = Math.round(sqrtPrice);

  // H26 method: digit sum of rounded sqrt
  const digitSumSqrt = digitSum(roundedSqrt);
  const h26Base = sqrtPrice * digitSumSqrt;
  const h26Full = h26Base * multiplier;

  // F4 method: digit sum of price
  const digitSumPrice = digitSum(price);
  const f4Base = sqrtPrice * digitSumPrice;
  const f4Full = f4Base * multiplier;

  const levels = [];

  LEVEL_PERCENTAGES.forEach(pct => {
    const factor = pct / 100;

    // H26 (Swing) levels
    levels.push({
      method: 'H26 (Swing)',
      pct,
      direction: 'up',
      value: price + (h26Full * factor)
    });
    levels.push({
      method: 'H26 (Swing)',
      pct,
      direction: 'down',
      value: price - (h26Full * factor)
    });

    // F4 (Extended) levels
    levels.push({
      method: 'F4 (Extended)',
      pct,
      direction: 'up',
      value: price + (f4Full * factor)
    });
    levels.push({
      method: 'F4 (Extended)',
      pct,
      direction: 'down',
      value: price - (f4Full * factor)
    });
  });

  return levels;
};

// Helper: Find all matching input prices for a target range
const findInputPricesForRange = (minTarget, maxTarget, multiplier = 7, searchMin = null, searchMax = null, stepSize = null) => {
  // Auto-calculate search range if not provided
  const targetMid = (minTarget + maxTarget) / 2;
  const searchStart = searchMin || Math.max(1, targetMid * 0.3);
  const searchEnd = searchMax || targetMid * 3;
  const step = stepSize || Math.max(1, Math.floor((searchEnd - searchStart) / 10000));

  const matches = [];
  const seenPrices = new Set();

  for (let inputPrice = searchStart; inputPrice <= searchEnd; inputPrice += step) {
    const levels = calculateGannLevels(inputPrice, multiplier);

    for (const level of levels) {
      if (level.value >= minTarget && level.value <= maxTarget) {
        // Round to avoid duplicates
        const roundedInput = Math.round(inputPrice * 100) / 100;
        const key = `${roundedInput}-${level.method}-${level.pct}-${level.direction}`;

        if (!seenPrices.has(key)) {
          seenPrices.add(key);
          matches.push({
            inputPrice: roundedInput,
            method: level.method,
            percentage: level.pct,
            direction: level.direction,
            levelValue: Math.round(level.value * 100) / 100
          });
        }
      }
    }
  }

  // Sort by input price
  matches.sort((a, b) => a.inputPrice - b.inputPrice);

  return matches;
};

function GannCalculator() {
  const [price, setPrice] = useState(() => {
    const saved = localStorage.getItem(GANN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.price || '';
    }
    return '';
  });
  const [customMultiplier, setCustomMultiplier] = useState(() => {
    const saved = localStorage.getItem(GANN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.customMultiplier || '';
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(() => {
    const saved = localStorage.getItem(GANN_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.result || null;
    }
    return null;
  });
  const [copied, setCopied] = useState(null);
  const [showSubRanges, setShowSubRanges] = useState(false);

  // Reverse Engineering State
  const [showReverseSection, setShowReverseSection] = useState(() => {
    const saved = localStorage.getItem(REVERSE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.showSection || false;
    }
    return false;
  });
  const [reverseMinTarget, setReverseMinTarget] = useState(() => {
    const saved = localStorage.getItem(REVERSE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.minTarget || '';
    }
    return '';
  });
  const [reverseMaxTarget, setReverseMaxTarget] = useState(() => {
    const saved = localStorage.getItem(REVERSE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.maxTarget || '';
    }
    return '';
  });
  const [reverseMultiplier, setReverseMultiplier] = useState(() => {
    const saved = localStorage.getItem(REVERSE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.multiplier || '7';
    }
    return '7';
  });
  const [reverseSearchMin, setReverseSearchMin] = useState('');
  const [reverseSearchMax, setReverseSearchMax] = useState('');
  const [reverseResults, setReverseResults] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState(null);
  const [filterMethod, setFilterMethod] = useState('all'); // 'all', 'h26', 'f4'
  const [filterDirection, setFilterDirection] = useState('all'); // 'all', 'up' (target in down ranges), 'down' (target in up ranges)
  const [filterInputMin, setFilterInputMin] = useState(''); // Filter input prices min
  const [filterInputMax, setFilterInputMax] = useState(''); // Filter input prices max

  useEffect(() => {
    localStorage.setItem(GANN_STORAGE_KEY, JSON.stringify({ price, customMultiplier, result }));
  }, [price, customMultiplier, result]);

  // Persist reverse engineering state
  useEffect(() => {
    localStorage.setItem(REVERSE_STORAGE_KEY, JSON.stringify({
      showSection: showReverseSection,
      minTarget: reverseMinTarget,
      maxTarget: reverseMaxTarget,
      multiplier: reverseMultiplier
    }));
  }, [showReverseSection, reverseMinTarget, reverseMaxTarget, reverseMultiplier]);

  // Reverse engineering search handler
  const handleReverseSearch = useCallback(() => {
    const minVal = parseFloat(reverseMinTarget);
    const maxVal = parseFloat(reverseMaxTarget);
    const mult = parseFloat(reverseMultiplier) || 7;

    if (isNaN(minVal) || isNaN(maxVal)) {
      setReverseError('Please enter valid target range values');
      return;
    }

    if (minVal >= maxVal) {
      setReverseError('Minimum target must be less than maximum target');
      return;
    }

    setReverseLoading(true);
    setReverseError(null);

    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      try {
        const searchMin = reverseSearchMin ? parseFloat(reverseSearchMin) : null;
        const searchMax = reverseSearchMax ? parseFloat(reverseSearchMax) : null;

        const results = findInputPricesForRange(minVal, maxVal, mult, searchMin, searchMax);
        setReverseResults(results);
      } catch (err) {
        setReverseError(err.message);
      } finally {
        setReverseLoading(false);
      }
    }, 10);
  }, [reverseMinTarget, reverseMaxTarget, reverseMultiplier, reverseSearchMin, reverseSearchMax]);

  // Handle Enter key in reverse inputs
  const handleReverseKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleReverseSearch();
    }
  };

  // Filter reverse results
  const filteredReverseResults = useMemo(() => {
    if (!reverseResults) return null;

    const inputMin = filterInputMin ? parseFloat(filterInputMin) : null;
    const inputMax = filterInputMax ? parseFloat(filterInputMax) : null;

    return reverseResults.filter(r => {
      // Filter by input price range
      if (inputMin !== null && r.inputPrice < inputMin) return false;
      if (inputMax !== null && r.inputPrice > inputMax) return false;

      if (filterMethod !== 'all') {
        const methodMatch = filterMethod === 'h26' ? r.method.includes('H26') : r.method.includes('F4');
        if (!methodMatch) return false;
      }
      // Inverse logic: "Up" means you go UP to target, so target is in DOWN ranges (input price > target)
      // "Down" means you go DOWN to target, so target is in UP ranges (input price < target)
      if (filterDirection === 'up' && r.direction !== 'down') return false;
      if (filterDirection === 'down' && r.direction !== 'up') return false;
      return true;
    });
  }, [reverseResults, filterMethod, filterDirection, filterInputMin, filterInputMax]);

  // Group results by input price for cleaner display
  const groupedReverseResults = useMemo(() => {
    if (!filteredReverseResults) return null;

    const groups = {};
    filteredReverseResults.forEach(r => {
      if (!groups[r.inputPrice]) {
        groups[r.inputPrice] = [];
      }
      groups[r.inputPrice].push(r);
    });

    return Object.entries(groups).map(([inputPrice, matches]) => ({
      inputPrice: parseFloat(inputPrice),
      matches
    }));
  }, [filteredReverseResults]);

  const calculateLevels = async () => {
    if (!price || isNaN(parseFloat(price))) {
      setError('Please enter a valid price');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = { price: parseFloat(price) };
      
      if (customMultiplier && !isNaN(parseFloat(customMultiplier))) {
        payload.custom_multiplier = parseFloat(customMultiplier);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/gann/levels`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to calculate levels');
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (value, label) => {
    navigator.clipboard.writeText(value.toString());
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };

  const formatPrice = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      calculateLevels();
    }
  };

  // Calculate sub-ranges from main levels
  const calculateSubRanges = (levels, inputPrice) => {
    if (!levels || !inputPrice) return {};
    
    // Base unit is the 25% range
    const baseRange = levels['25'].range;
    
    return {
      '12.5': {
        up: inputPrice + (baseRange * 0.5),
        down: inputPrice - (baseRange * 0.5),
        range: baseRange * 0.5
      },
      '37.5': {
        up: inputPrice + (baseRange * 1.5),
        down: inputPrice - (baseRange * 1.5),
        range: baseRange * 1.5
      },
      '62.5': {
        up: inputPrice + (baseRange * 2.5),
        down: inputPrice - (baseRange * 2.5),
        range: baseRange * 2.5
      },
      '87.5': {
        up: inputPrice + (baseRange * 3.5),
        down: inputPrice - (baseRange * 3.5),
        range: baseRange * 3.5
      }
    };
  };

  // Calculate sub-ranges for all level types
  const swingSubRanges = useMemo(() => 
    calculateSubRanges(result?.method2_h26?.levels, result?.input_price), 
    [result]
  );
  
  const extendedSubRanges = useMemo(() => 
    calculateSubRanges(result?.method1_f4?.levels, result?.input_price), 
    [result]
  );

  const customSwingSubRanges = useMemo(() => 
    calculateSubRanges(result?.custom?.h26_method?.levels, result?.input_price), 
    [result]
  );

  const customExtendedSubRanges = useMemo(() => 
    calculateSubRanges(result?.custom?.f4_method?.levels, result?.input_price), 
    [result]
  );

  // Collect ALL values (main + sub) for matching
  const collectAllValues = (mainLevels, subLevels) => {
    const values = new Set();
    if (mainLevels) {
      Object.values(mainLevels).forEach(l => {
        values.add(Math.round(l.up * 100) / 100);
        values.add(Math.round(l.down * 100) / 100);
      });
    }
    if (subLevels) {
      Object.values(subLevels).forEach(l => {
        values.add(Math.round(l.up * 100) / 100);
        values.add(Math.round(l.down * 100) / 100);
      });
    }
    return values;
  };

  // All Extended values (main + sub) for matching Swing
  const allExtendedValues = useMemo(() => 
    collectAllValues(result?.method1_f4?.levels, extendedSubRanges),
    [result, extendedSubRanges]
  );

  // All Swing values (main + sub) for matching Extended
  const allSwingValues = useMemo(() => 
    collectAllValues(result?.method2_h26?.levels, swingSubRanges),
    [result, swingSubRanges]
  );

  // All Custom Extended values
  const allCustomExtendedValues = useMemo(() => 
    collectAllValues(result?.custom?.f4_method?.levels, customExtendedSubRanges),
    [result, customExtendedSubRanges]
  );

  // All Custom Swing values
  const allCustomSwingValues = useMemo(() => 
    collectAllValues(result?.custom?.h26_method?.levels, customSwingSubRanges),
    [result, customSwingSubRanges]
  );

  // Check if value matches (0.5% tolerance) and return matching value
  const findMatchingValue = (value, targetSet, inputPrice) => {
    if (!value || value === inputPrice) return null;
    const roundedValue = Math.round(value * 100) / 100;
    for (const targetVal of targetSet) {
      const tolerance = Math.max(targetVal, roundedValue) * 0.005;
      if (Math.abs(targetVal - roundedValue) <= tolerance) return targetVal;
    }
    return null;
  };

  // Check if value matches (0.5% tolerance)
  const valueMatches = (value, targetSet, inputPrice) => {
    return findMatchingValue(value, targetSet, inputPrice) !== null;
  };

  // Build emoji map for all matching pairs (standard levels)
  const standardEmojiMap = useMemo(() => {
    if (!result) return new Map();
    
    const emojiMap = new Map();
    let emojiIndex = 0;
    
    // Collect all swing values with their keys
    const swingValues = [];
    if (result.method2_h26?.levels) {
      Object.entries(result.method2_h26.levels).forEach(([pct, level]) => {
        swingValues.push({ key: `swing-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        swingValues.push({ key: `swing-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      });
    }
    Object.entries(swingSubRanges).forEach(([pct, level]) => {
      if (level) {
        swingValues.push({ key: `swing-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        swingValues.push({ key: `swing-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      }
    });
    
    // Collect all extended values with their keys
    const extendedValues = [];
    if (result.method1_f4?.levels) {
      Object.entries(result.method1_f4.levels).forEach(([pct, level]) => {
        extendedValues.push({ key: `ext-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        extendedValues.push({ key: `ext-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      });
    }
    Object.entries(extendedSubRanges).forEach(([pct, level]) => {
      if (level) {
        extendedValues.push({ key: `ext-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        extendedValues.push({ key: `ext-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      }
    });
    
    // Find matching pairs and assign emojis
    swingValues.forEach(swing => {
      if (swing.value === result.input_price) return;
      
      extendedValues.forEach(extended => {
        if (extended.value === result.input_price) return;
        
        const tolerance = Math.max(swing.value, extended.value) * 0.005;
        if (Math.abs(swing.value - extended.value) <= tolerance) {
          // Found a match! Assign same emoji to both
          if (!emojiMap.has(swing.key)) {
            const emoji = MATCH_EMOJIS[emojiIndex % MATCH_EMOJIS.length];
            emojiMap.set(swing.key, emoji);
            emojiMap.set(extended.key, emoji);
            emojiIndex++;
          }
        }
      });
    });
    
    return emojiMap;
  }, [result, swingSubRanges, extendedSubRanges]);

  // Build emoji map for custom levels
  const customEmojiMap = useMemo(() => {
    if (!result?.custom) return new Map();
    
    const emojiMap = new Map();
    let emojiIndex = 0;
    
    // Collect all custom swing values
    const customSwingValues = [];
    if (result.custom.h26_method?.levels) {
      Object.entries(result.custom.h26_method.levels).forEach(([pct, level]) => {
        customSwingValues.push({ key: `custom-h26-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        customSwingValues.push({ key: `custom-h26-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      });
    }
    Object.entries(customSwingSubRanges).forEach(([pct, level]) => {
      if (level) {
        customSwingValues.push({ key: `custom-h26-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        customSwingValues.push({ key: `custom-h26-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      }
    });
    
    // Collect all custom extended values
    const customExtendedValues = [];
    if (result.custom.f4_method?.levels) {
      Object.entries(result.custom.f4_method.levels).forEach(([pct, level]) => {
        customExtendedValues.push({ key: `custom-f4-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        customExtendedValues.push({ key: `custom-f4-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      });
    }
    Object.entries(customExtendedSubRanges).forEach(([pct, level]) => {
      if (level) {
        customExtendedValues.push({ key: `custom-f4-${pct}-up`, value: Math.round(level.up * 100) / 100 });
        customExtendedValues.push({ key: `custom-f4-${pct}-down`, value: Math.round(level.down * 100) / 100 });
      }
    });
    
    // Find matching pairs and assign emojis
    customSwingValues.forEach(swing => {
      if (swing.value === result.input_price) return;
      
      customExtendedValues.forEach(extended => {
        if (extended.value === result.input_price) return;
        
        const tolerance = Math.max(swing.value, extended.value) * 0.005;
        if (Math.abs(swing.value - extended.value) <= tolerance) {
          if (!emojiMap.has(swing.key)) {
            const emoji = MATCH_EMOJIS[emojiIndex % MATCH_EMOJIS.length];
            emojiMap.set(swing.key, emoji);
            emojiMap.set(extended.key, emoji);
            emojiIndex++;
          }
        }
      });
    });
    
    return emojiMap;
  }, [result, customSwingSubRanges, customExtendedSubRanges]);

  // Check if any sub-range value matches (to auto-show sub-ranges)
  const hasSubRangeMatch = useMemo(() => {
    if (!result) return false;
    
    // Check swing sub-ranges against extended
    for (const pct of SUB_PCTS) {
      if (swingSubRanges[pct]) {
        if (valueMatches(swingSubRanges[pct].up, allExtendedValues, result.input_price)) return true;
        if (valueMatches(swingSubRanges[pct].down, allExtendedValues, result.input_price)) return true;
      }
      if (extendedSubRanges[pct]) {
        if (valueMatches(extendedSubRanges[pct].up, allSwingValues, result.input_price)) return true;
        if (valueMatches(extendedSubRanges[pct].down, allSwingValues, result.input_price)) return true;
      }
    }
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, swingSubRanges, extendedSubRanges, allExtendedValues, allSwingValues]);



  // Render level row with match indicators (emojis)
  const renderLevelRow = (pct, level, prefix, shimmerFn, emojiMap, isSub = false) => {
    const upKey = `${prefix}-${pct}-up`;
    const downKey = `${prefix}-${pct}-down`;
    const upEmoji = emojiMap.get(upKey);
    const downEmoji = emojiMap.get(downKey);
    
    return (
      <div key={`${prefix}-${pct}`} className={`level-row ${pct === '100' ? 'strong' : ''} ${isSub ? 'sub-range' : ''}`}>
        <span className="level-pct">{pct}%</span>
        <span className="level-value resistance clickable" onClick={() => copyToClipboard(level.up, upKey)}>
          <span className={shimmerFn(level.up) ? 'sparkle-shimmer' : ''}>
            {formatPrice(level.up)}
          </span>
          {upEmoji && <span className="match-emoji">{upEmoji}</span>}
          {copied === upKey ? <Check size={12} /> : <Copy size={12} />}
        </span>
        <span className="level-range">
          {formatPrice(level.range)}
        </span>
        <span className="level-value support clickable" onClick={() => copyToClipboard(level.down, downKey)}>
          <span className={shimmerFn(level.down) ? 'sparkle-shimmer' : ''}>
            {formatPrice(level.down)}
          </span>
          {downEmoji && <span className="match-emoji">{downEmoji}</span>}
          {copied === downKey ? <Check size={12} /> : <Copy size={12} />}
        </span>
      </div>
    );
  };

  // Render table with main levels and optional sub-ranges
  const renderLevelsTable = (mainLevels, subLevels, prefix, shimmerFn, emojiMap) => {
    // Interleave main and sub levels in descending order
    const allPcts = ['100', '87.5', '75', '62.5', '50', '37.5', '25', '12.5'];
    
    return (
      <div className="levels-table">
        <div className="levels-header">
          <span>Level</span>
          <span className="resistance">Resistance (UP)</span>
          <span>Range</span>
          <span className="support">Support (DOWN)</span>
        </div>

        {allPcts.map((pct) => {
          const isSub = SUB_PCTS.includes(pct);
          const level = isSub ? subLevels[pct] : mainLevels[pct];
          
          if (!level) return null;
          if (isSub && !showSubRanges) return null;
          
          return renderLevelRow(pct, level, prefix, shimmerFn, emojiMap, isSub);
        })}
      </div>
    );
  };

  // Shimmer functions
  const swingMatchesExtended = (value) => valueMatches(value, allExtendedValues, result?.input_price);
  const extendedMatchesSwing = (value) => valueMatches(value, allSwingValues, result?.input_price);
  const customSwingMatchesExtended = (value) => valueMatches(value, allCustomExtendedValues, result?.input_price);
  const customExtendedMatchesSwing = (value) => valueMatches(value, allCustomSwingValues, result?.input_price);

  return (
    <div className="gann-calculator">
      <div className="gann-header">
        <Calculator size={24} />
        <h2>Levels Calculator</h2>
      </div>

      <div className="gann-input-section">
        <div className="input-row">
          <div className="input-group">
            <label>Enter Price</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., 94760"
              step="any"
            />
          </div>
          <button 
            onClick={calculateLevels} 
            disabled={loading}
            className="calculate-btn"
          >
            {loading ? <RefreshCw size={16} className="spinning" /> : 'Calculate'}
          </button>
        </div>
        {error && <div className="error-message">{error}</div>}
      </div>

      {/* Reverse Engineering Section */}
      <div className="reverse-engineering-section">
        <button
          className={`toggle-btn reverse-toggle ${showReverseSection ? 'active' : ''}`}
          onClick={() => setShowReverseSection(!showReverseSection)}
        >
          <Target size={16} />
          {showReverseSection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          Reverse Engineering — Find Input Prices for Target Levels
        </button>

        {showReverseSection && (
          <div className="reverse-content">
            <p className="reverse-description">
              Enter a target price range to find all input prices whose Gann levels fall within that range.
            </p>

            <div className="reverse-inputs">
              <div className="reverse-input-row">
                <div className="input-group">
                  <label>Target Min</label>
                  <input
                    type="number"
                    value={reverseMinTarget}
                    onChange={(e) => setReverseMinTarget(e.target.value)}
                    onKeyPress={handleReverseKeyPress}
                    placeholder="e.g., 77000"
                    step="any"
                  />
                </div>
                <div className="input-group">
                  <label>Target Max</label>
                  <input
                    type="number"
                    value={reverseMaxTarget}
                    onChange={(e) => setReverseMaxTarget(e.target.value)}
                    onKeyPress={handleReverseKeyPress}
                    placeholder="e.g., 79000"
                    step="any"
                  />
                </div>
                <div className="input-group">
                  <label>Multiplier</label>
                  <input
                    type="number"
                    value={reverseMultiplier}
                    onChange={(e) => setReverseMultiplier(e.target.value)}
                    onKeyPress={handleReverseKeyPress}
                    placeholder="7"
                    step="any"
                  />
                </div>
              </div>

              <div className="reverse-filters-section">
                <div className="input-group">
                  <label>Method</label>
                  <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
                    <option value="all">All Methods</option>
                    <option value="h26">H26 (Swing) Only</option>
                    <option value="f4">F4 (Extended) Only</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>Direction</label>
                  <select value={filterDirection} onChange={(e) => setFilterDirection(e.target.value)}>
                    <option value="all">Both Directions</option>
                    <option value="up">Go UP to Target (Support)</option>
                    <option value="down">Go DOWN to Target (Resistance)</option>
                  </select>
                </div>
              </div>

              <div className="reverse-input-filter">
                <span className="filter-label">Filter Input Prices:</span>
                <div className="input-filter-row">
                  <div className="input-group">
                    <label>Input Min</label>
                    <input
                      type="number"
                      value={filterInputMin}
                      onChange={(e) => setFilterInputMin(e.target.value)}
                      placeholder="e.g., 101400"
                      step="any"
                    />
                  </div>
                  <span className="range-separator">—</span>
                  <div className="input-group">
                    <label>Input Max</label>
                    <input
                      type="number"
                      value={filterInputMax}
                      onChange={(e) => setFilterInputMax(e.target.value)}
                      placeholder="e.g., 103400"
                      step="any"
                    />
                  </div>
                  {(filterInputMin || filterInputMax) && (
                    <button
                      className="clear-filter-btn"
                      onClick={() => {
                        setFilterInputMin('');
                        setFilterInputMax('');
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="reverse-advanced">
                <details>
                  <summary>Advanced: Custom Search Range</summary>
                  <div className="advanced-inputs">
                    <div className="input-group">
                      <label>Search Min Price</label>
                      <input
                        type="number"
                        value={reverseSearchMin}
                        onChange={(e) => setReverseSearchMin(e.target.value)}
                        placeholder="Auto-calculated"
                        step="any"
                      />
                    </div>
                    <div className="input-group">
                      <label>Search Max Price</label>
                      <input
                        type="number"
                        value={reverseSearchMax}
                        onChange={(e) => setReverseSearchMax(e.target.value)}
                        placeholder="Auto-calculated"
                        step="any"
                      />
                    </div>
                  </div>
                </details>
              </div>

              <button
                onClick={handleReverseSearch}
                disabled={reverseLoading}
                className="calculate-btn reverse-btn"
              >
                {reverseLoading ? <RefreshCw size={16} className="spinning" /> : <Search size={16} />}
                Find Input Prices
              </button>
            </div>

            {reverseError && <div className="error-message">{reverseError}</div>}

            {reverseResults && (
              <div className="reverse-results">
                <div className="reverse-results-header">
                  <h4>
                    <Target size={16} />
                    Found {filteredReverseResults?.length || 0} matching levels from {groupedReverseResults?.length || 0} input prices
                  </h4>
                </div>

                {groupedReverseResults && groupedReverseResults.length > 0 ? (
                  <div className="reverse-results-grid">
                    {groupedReverseResults.slice(0, 100).map((group, idx) => (
                      <div key={idx} className="reverse-result-card">
                        <div className="result-input-price">
                          <span className="label">Input Price:</span>
                          <span
                            className="value clickable"
                            onClick={() => {
                              setPrice(group.inputPrice.toString());
                              copyToClipboard(group.inputPrice, `input-${idx}`);
                            }}
                          >
                            {formatPrice(group.inputPrice)}
                            {copied === `input-${idx}` ? <Check size={12} /> : <Copy size={12} />}
                          </span>
                        </div>
                        <div className="result-matches">
                          {group.matches.map((m, mIdx) => (
                            <div key={mIdx} className={`result-match ${m.direction}`}>
                              <span className="match-method">{m.method}</span>
                              <span className="match-pct">{m.percentage}%</span>
                              <span className={`match-direction ${m.direction}`}>
                                {m.direction === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              </span>
                              <span
                                className="match-value clickable"
                                onClick={() => copyToClipboard(m.levelValue, `match-${idx}-${mIdx}`)}
                              >
                                {formatPrice(m.levelValue)}
                                {copied === `match-${idx}-${mIdx}` ? <Check size={10} /> : <Copy size={10} />}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    No input prices found that generate levels within the target range.
                    Try expanding your search range or adjusting the multiplier.
                  </div>
                )}

                {groupedReverseResults && groupedReverseResults.length > 100 && (
                  <div className="results-truncated">
                    Showing first 100 of {groupedReverseResults.length} input prices.
                    Narrow your target range for more specific results.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {result && (
        <>
          {/* Sub-ranges toggle */}
          <div className="sub-range-toggle">
            <button 
              className={`toggle-btn ${showSubRanges ? 'active' : ''}`}
              onClick={() => setShowSubRanges(!showSubRanges)}
            >
              {showSubRanges ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {showSubRanges ? 'Hide' : 'Show'} Sub-Ranges (12.5%, 37.5%, 62.5%, 87.5%)
              {hasSubRangeMatch && !showSubRanges && <span className="match-badge">Match Found!</span>}
            </button>
          </div>

          {/* Swing Levels */}
          <div className="levels-card primary-levels">
            <h3>
              <TrendingUp size={18} className="icon-up" />
              Swing Levels — 4H / Daily / Weekly Ranges
              <TrendingDown size={18} className="icon-down" />
            </h3>
            {renderLevelsTable(result.method2_h26.levels, swingSubRanges, 'swing', swingMatchesExtended, standardEmojiMap)}
          </div>

          {/* Extended Levels */}
          <div className="levels-card secondary-levels">
            <h3>
              <TrendingUp size={18} className="icon-up" />
              Extended Levels — Weekly / Monthly Ranges
              <TrendingDown size={18} className="icon-down" />
            </h3>
            {renderLevelsTable(result.method1_f4.levels, extendedSubRanges, 'ext', extendedMatchesSwing, standardEmojiMap)}
          </div>

          {/* Chart with Auto-Drawn Gann Levels */}
          <LevelChart 
            title="Levels Chart (Auto-Drawn)"
            chartId="standard"
            levels={{
              swing: result.method2_h26?.levels,
              extended: result.method1_f4?.levels,
            }}
            subLevels={showSubRanges ? {
              swing: swingSubRanges,
              extended: extendedSubRanges,
            } : null}
            inputPrice={result?.input_price}
          />

          {/* Custom Multiplier Section */}
          <div className="custom-multiplier-section">
            <div className="custom-mult-header">
              <Sliders size={18} />
              <span>Custom Multiplier</span>
            </div>
            <div className="custom-mult-input">
              <input
                type="number"
                value={customMultiplier}
                onChange={(e) => setCustomMultiplier(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter multiplier (e.g., 3, 5, 9)"
                step="any"
              />
              <button 
                onClick={calculateLevels} 
                disabled={loading || !customMultiplier}
                className="calculate-custom-btn"
              >
                {loading ? <RefreshCw size={14} className="spinning" /> : 'Calculate Custom'}
              </button>
            </div>
          </div>

          {/* Custom Levels */}
          {result.custom && (
            <>
              <div className="levels-card custom-levels">
                <h3>
                  <Sliders size={18} className="icon-custom" />
                  Custom Levels
                </h3>
                
                <div className="custom-tables">
                  <div className="custom-table-section">
                    <h4>Custom Swing Levels — 4H / Daily / Weekly Ranges (×{result.custom.multiplier})</h4>
                    {renderLevelsTable(result.custom.h26_method.levels, customSwingSubRanges, 'custom-h26', customSwingMatchesExtended, customEmojiMap)}
                  </div>
                  
                  <div className="custom-table-section">
                    <h4>Custom Extended Levels — Weekly / Monthly Ranges (×{result.custom.multiplier})</h4>
                    {renderLevelsTable(result.custom.f4_method.levels, customExtendedSubRanges, 'custom-f4', customExtendedMatchesSwing, customEmojiMap)}
                  </div>
                </div>
              </div>

              {/* Chart with Auto-Drawn Custom Levels */}
              <LevelChart 
                title={`Custom Levels Chart (×${result.custom.multiplier}) - Auto-Drawn`}
                chartId="custom"
                levels={{
                  swing: result.custom.h26_method?.levels,
                  extended: result.custom.f4_method?.levels,
                }}
                subLevels={showSubRanges ? {
                  swing: customSwingSubRanges,
                  extended: customExtendedSubRanges,
                } : null}
                inputPrice={result?.input_price}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default GannCalculator;
